import type { TrendPoint } from '../analyticsApi';

const W = 720;
const H = 200;
const PAD = 24;

// Visitors only. Pageviews has a different scale and lives in a stat tile — two
// y-axes on one chart is the mistake this deliberately avoids.
export default function TrendChart({ points }: { points: TrendPoint[] }) {
  if (points.length === 0) {
    return (
      <section className="rounded-2xl bg-white p-5 shadow-card">
        <h3 className="font-nunito text-sm text-ink-soft">Visitors over time</h3>
        <p className="mt-6 font-nunito text-sm text-meta">No data yet</p>
      </section>
    );
  }

  const max = Math.max(...points.map((p) => p.visitors), 1);
  const x = (i: number) => PAD + (i / Math.max(points.length - 1, 1)) * (W - PAD * 2);
  const y = (v: number) => H - PAD - (v / max) * (H - PAD * 2);

  const line = points.map((p, i) => `${x(i).toFixed(1)},${y(p.visitors).toFixed(1)}`).join(' ');
  const area = `${PAD},${H - PAD} ${line} ${x(points.length - 1).toFixed(1)},${H - PAD}`;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-card">
      {/* One series, so the title carries identity and no legend box is needed. */}
      <h3 className="font-nunito text-sm text-ink-soft">Visitors over time</h3>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 w-full"
        role="img"
        aria-label="Visitors over time"
      >
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#e3e8d0" strokeWidth="1" />
        <polygon points={area} fill="#9db39f" fillOpacity="0.18" />
        <polyline
          points={line}
          fill="none"
          stroke="#7e957f"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((p, i) => (
          <circle key={p.date} cx={x(i)} cy={y(p.visitors)} r="4" fill="#5c7a5e">
            <title>{`${p.date}: ${p.visitors} visitors`}</title>
          </circle>
        ))}
      </svg>

      {/* The table is the accessible view of the same data — identity and value
          are never carried by the drawing alone. */}
      <table className="sr-only" aria-label="Visitors over time">
        <thead>
          <tr>
            <th>Date</th>
            <th>Visitors</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.date}>
              <td>{p.date}</td>
              <td>{p.visitors}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
